import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

// Note: yet-another-react-lightbox is dynamically imported in useEffect
// to avoid SSR issues (the library requires browser APIs)

import css from './ListingImageGallery.module.css';

const PortalToLightboxContainer = ({ children }) => {
  const [container, setContainer] = useState(null);

  useEffect(() => {
    // The Lightbox component renders via a Portal, so its container might not be immediately available in the DOM.
    // We poll briefly to find the .yarl__container element.
    let attempts = 0;
    const interval = setInterval(() => {
      const el = document.querySelector('.yarl__container');
      if (el) {
        setContainer(el);
        clearInterval(interval);
      }
      attempts++;
      if (attempts > 20) { // Stop after ~1 second
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, []);

  if (!container) return null;
  return ReactDOM.createPortal(children, container);
};

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: false,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

// Helper to get image URL from image object
const getImageUrl = (image, variant) => {
  return image?.attributes?.variants?.[variant]?.url || '';
};

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
      ? { aspectWidth: 4, aspectHeight: 3 }
      : hasDimensions
        ? { aspectWidth: w, aspectHeight: h }
        : { aspectWidth: 1, aspectHeight: 1 };
};

const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Dynamic loading state for Lightbox (SSR-safe)
  const [LightboxComponent, setLightboxComponent] = useState(null);
  const [lightboxPlugins, setLightboxPlugins] = useState({ Zoom: null, Thumbnails: null });

  // Ref for zoom control
  const zoomRef = useRef(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const isProcessingRef = useRef(false);
  const lastClickTimeRef = useRef(0);
  const isDraggingRef = useRef(false);
  const mouseDownPosRef = useRef({ x: 0, y: 0 });

  // Track mouse down position to detect drags
  useEffect(() => {
    const handleMouseDown = (e) => {
      mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
      isDraggingRef.current = false;
    };

    const handleMouseMove = (e) => {
      // If mouse moved more than 10 pixels, it's a drag
      const dx = Math.abs(e.clientX - mouseDownPosRef.current.x);
      const dy = Math.abs(e.clientY - mouseDownPosRef.current.y);
      if (dx > 10 || dy > 10) {
        isDraggingRef.current = true;
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Handler for single click zoom toggle
  const handleZoomClick = useCallback(() => {
    // Ignore if this was a drag operation (panning while zoomed)
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      return;
    }

    // Debounce - ignore rapid clicks
    const now = Date.now();
    if (now - lastClickTimeRef.current < 350) {
      return;
    }
    lastClickTimeRef.current = now;

    // Prevent multiple simultaneous triggers
    if (isProcessingRef.current) {
      return;
    }

    if (!zoomRef.current) {
      return;
    }

    isProcessingRef.current = true;

    // Toggle zoom based on current state
    if (isZoomed) {
      // Zoom out to normal (1x)
      zoomRef.current.changeZoom(1, true);
      setIsZoomed(false);
    } else {
      // Zoom in to 2.5x
      zoomRef.current.changeZoom(2.5, true);
      setIsZoomed(true);
    }

    // Reset processing flag after a delay
    setTimeout(() => {
      isProcessingRef.current = false;
    }, 400);
  }, [isZoomed]);

  // Dynamically import Lightbox only on client side to avoid SSR issues
  useEffect(() => {
    Promise.all([
      import('yet-another-react-lightbox'),
      import('yet-another-react-lightbox/plugins/zoom'),
      import('yet-another-react-lightbox/plugins/thumbnails'),
      import('yet-another-react-lightbox/styles.css'),
      import('yet-another-react-lightbox/plugins/thumbnails.css'),
    ]).then(([lightbox, zoom, thumbnails]) => {
      setLightboxComponent(() => lightbox.default);
      setLightboxPlugins({ Zoom: zoom.default, Thumbnails: thumbnails.default });
    });
  }, []);

  // Auto-scroll active thumbnail into view
  useEffect(() => {
    if (lightboxOpen) {
      // Use a small delay to ensure the thumbnail strip is fully rendered in the DOM
      // This is especially important when the lightbox first opens with a thumbnail
      // that is at the edge of the visible area
      const scrollToActiveThumb = () => {
        const activeThumb = document.getElementById(`listing-lightbox-thumb-${lightboxIndex}`);
        if (activeThumb) {
          activeThumb.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      };

      // Small delay to ensure DOM is ready, especially when lightbox first opens
      const timeoutId = setTimeout(scrollToActiveThumb, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [lightboxIndex, lightboxOpen]);

  const { intl, rootClassName, className, images, imageVariants, thumbnailVariants, isPromotedUser } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);

  // Prepare slides for lightbox - use the largest variant
  const largestVariant = imageVariants[imageVariants.length - 1];
  const slides = images.map((img, i) => ({
    src: getImageUrl(img, largestVariant),
    alt: intl.formatMessage(
      { id: 'ListingImageGallery.imageAltText' },
      { index: i + 1, count: images.length }
    ),
    width: img?.attributes?.variants?.[largestVariant]?.width || 1920,
    height: img?.attributes?.variants?.[largestVariant]?.height || 1280,
  }));

  const openLightbox = useCallback((index = 0) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
      index: i,
    };
  });
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };

  const renderItem = item => {
    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div
          className={css.itemCentering}
          onClick={() => openLightbox(item.index)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && openLightbox(item.index)}
          style={{ cursor: 'zoom-in' }}
        >
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
          />
          {isPromotedUser && !isFullscreen && (
            <div className={css.promotedUserTag}>
              <span className={css.promotedUserTagText}>
                Dora's<br />Closet
              </span>
            </div>
          )}
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    return (
      <div>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
          draggable={false}
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };


  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <>
      <ReactImageGallery
        additionalClass={classes}
        items={items}
        renderItem={renderItem}
        renderThumbInner={renderThumbInner}
        onScreenChange={onScreenChange}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        showFullscreenButton={false}
        {...IMAGE_GALLERY_OPTIONS}
      />

      {/* Lightbox for enlarged view with zoom - only rendered when dynamically loaded */}
      {LightboxComponent && lightboxPlugins.Zoom && (
        <div className={css.lightboxWrapper}>
          <LightboxComponent
            open={lightboxOpen}
            close={() => setLightboxOpen(false)}
            index={lightboxIndex}
            slides={slides}
            plugins={[lightboxPlugins.Zoom]}
            zoom={{
              ref: zoomRef,
              maxZoomPixelRatio: 3,
              zoomInMultiplier: 2.5,
              doubleTapDelay: 0,
              doubleClickDelay: 0,
              doubleClickMaxStops: 0,
              keyboardMoveDistance: 50,
              wheelZoomDistanceFactor: 100,
              pinchZoomDistanceFactor: 100,
              scrollToZoom: false,
            }}
            animation={{ fade: 250, swipe: 250, zoom: 200 }}
            on={{
              click: handleZoomClick,
              view: ({ index }) => {
                // Update index when navigating via thumbnails or swipe
                setLightboxIndex(index);
                // Reset zoom state when changing slides
                setIsZoomed(false);
                isProcessingRef.current = false;
              },
            }}
            carousel={{ finite: false, preload: 2 }}
            controller={{ closeOnBackdropClick: true }}
            render={{
              buttonZoom: () => null,
            }}
            styles={{
              container: { backgroundColor: '#ffffff', paddingBottom: '140px' },
              root: { backgroundColor: '#ffffff' },
              slide: { cursor: isZoomed ? 'zoom-out' : 'zoom-in' },
              image: { cursor: isZoomed ? 'zoom-out' : 'zoom-in' },
              navigation: { backgroundColor: 'transparent' },
              navigationPrev: { backgroundColor: 'transparent' },
              navigationNext: { backgroundColor: 'transparent' },
            }}
          />
        </div>
      )}

      {/* Custom thumbnail strip - renders ALL thumbnails with scrolling via Portal (separate from lightbox) */}
      {/* Custom thumbnail strip - renders ALL thumbnails inside the Lightbox container */}
      {lightboxOpen && typeof document !== 'undefined' && (
        <PortalToLightboxContainer>
          <div
            ref={(node) => {
              if (node) {
                // We use a ref callback or layout effect to ensure native listeners are attached
                // with { passive: false } to allow preventing default if necessary, 
                // though here we mainly want to stop propagation to the lightbox.
                // React's onWheel is passive by default in some versions or delegates differently.
                // We also need to set touch-action programmatically or ensuring it works.
                node.style.touchAction = 'pan-x';

                const stopPropagation = (e) => e.stopPropagation();

                // Add native listeners to stop propagation to the lightbox container
                // using capture phase to ensure we catch it first, or bubble phase is fine if we stop it.
                // The lightbox likely listens on window or parent container.
                node.onwheel = stopPropagation;
                node.ontouchstart = stopPropagation;
                node.ontouchmove = stopPropagation;
                // node.ontouchend = stopPropagation; // Let click events flow if needed, but usually safe.
              }
            }}
            className={css.customThumbnailStrip}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={css.customThumbnailTrack}>
              {images.map((img, i) => (
                <button
                  key={i}
                  id={`listing-lightbox-thumb-${i}`}
                  type="button"
                  className={`${css.customThumbnail} ${i === lightboxIndex ? css.customThumbnailActive : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(i);
                  }}
                  aria-label={intl.formatMessage(
                    { id: 'ListingImageGallery.imageThumbnailAltText' },
                    { index: i + 1, count: images.length }
                  )}
                >
                  <ResponsiveImage
                    rootClassName={css.customThumbnailImage}
                    image={img}
                    alt=""
                    variants={thumbVariants}
                    sizes="88px"
                  />
                </button>
              ))}
            </div>
          </div>
        </PortalToLightboxContainer>
      )}
    </>
  );
};

ListingImageGallery.defaultProps = {
  rootClassName: null,
  className: null,
  thumbnailVariants: null,
  isPromotedUser: false,
};

const { string, arrayOf, bool } = PropTypes;

ListingImageGallery.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  imageVariants: arrayOf(string).isRequired,
  isPromotedUser: bool,
  thumbnailVariants: arrayOf(string),

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(ListingImageGallery);
